import { apiClient } from '../../api/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Flex, Grid, Text, VStack, Spinner, toaster, useT } from '@minetrace/ui'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LuArrowLeft,
  LuArrowUpRight,
  LuChevronRight,
  LuDownload,
  LuStamp,
  LuFileText,
  LuFolderOpen,
  LuShield,
  LuShieldCheck,
  LuBuilding2,
  LuUser,
  LuLandmark,
  LuMountain,
  LuMap,
  LuFlaskConical,
  LuScale,
  LuFilePen,
  LuUsers,
  LuKeyRound,
  LuReceipt,
  LuDatabase,
  LuClipboardCheck,
  LuPackage,
  LuFiles,
  LuFolderTree,
  LuTruck,
  LuCamera,
  LuRoute,
  LuWarehouse,
  LuHandCoins,
} from 'react-icons/lu'
import { expedienteService } from '../../api/services'
import type { ExpedienteDetailResponse, ExpedienteItemResponse } from '../../api/models/response/expediente'
import { EXPEDIENTE_ITEM_TYPE_BATCH_LINK } from '../../api/models/response/expediente'
import { accentFor, brand, fonts, mineral, shadows, themeColors } from '../../theme'
import { formatDateTime } from '../../lib/dates'
import AnimatedNumber from '../../components/brand/AnimatedNumber'

const MotionBox = motion.create(Box)
const PAGE_SIZE = 20

/** Expedientes vive en Identidad: rodocrosita. */
const acc = accentFor('identity')

const STATUS_CONFIG: Record<number, { label: string; bg: string; color: string }> = {
  0: { label: 'Borrador', bg: mineral.pizarraSoft, color: mineral.pizarra },
  1: { label: 'Activo', bg: mineral.sabanaSoft, color: mineral.sabanaDeep },
  2: { label: 'Revocado', bg: mineral.ajiSoft, color: mineral.aji },
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function folioLabel(n: number): string {
  return `${n} ${n === 1 ? 'folio' : 'folios'}`
}

/**
 * Color del nodo por FAMILIA de documento: la línea de tiempo se lee por bloques
 * (identidad, derecho minero, permisos, consultas, traslado, planta, liquidación)
 * sin tener que leer cada etiqueta. Es el mismo mineral que el módulo de origen.
 */
function itemColor(itemType: number): string {
  if (itemType === 0) return acc.color
  if (itemType === 131 || itemType === 61 || itemType === 71) return mineral.aji
  if (itemType >= 10 && itemType < 20) return mineral.lapis
  if (itemType >= 20 && itemType < 30) return mineral.cobre
  if (itemType >= 30 && itemType < 40) return mineral.amatista
  if (itemType >= 50 && itemType < 60) return mineral.turquesa
  if (itemType >= 80 && itemType < 100) return mineral.cobre
  if (itemType >= 100 && itemType < 110) return mineral.oro
  if (itemType === 120) return mineral.oro
  if (itemType >= 110 && itemType < 120) return mineral.turquesa
  if (itemType >= 130 && itemType < 140) return mineral.sabana
  if (itemType === 140) return mineral.cobre
  return mineral.pizarra
}

// Ícono por tipo de ítem del expediente (ExpedienteItemType). SVG (lucide), nunca
// emoji. Switch que devuelve JSX directo para no crear componentes en render.
function ItemTypeIcon({ itemType, size = 16 }: { itemType: number; size?: number }) {
  switch (itemType) {
    case 0: return <LuFolderOpen size={size} />
    case 1: case 2: return <LuShield size={size} />
    case 10: return <LuBuilding2 size={size} />
    case 11: return <LuUser size={size} />
    case 12: return <LuLandmark size={size} />
    case 14: return <LuKeyRound size={size} />
    case 15: return <LuFilePen size={size} />
    case 16: return <LuUsers size={size} />
    case 17: return <LuReceipt size={size} />
    case 20: case 21: return <LuMountain size={size} />
    case 22: return <LuMap size={size} />
    case 30: return <LuShield size={size} />
    case 32: return <LuClipboardCheck size={size} />
    case 33: case 36: case 37: return <LuFlaskConical size={size} />
    case 34: case 35: return <LuScale size={size} />
    case 50: case 51: case 52: case 53: return <LuDatabase size={size} />
    case 54: return <LuMap size={size} />
    case 70: case 71: return <LuFilePen size={size} />
    case 100: return <LuTruck size={size} />
    case 101: return <LuRoute size={size} />
    case 102: return <LuCamera size={size} />
    case 103: return <LuRoute size={size} />
    case 110: case 111: case 112: case 113: return <LuWarehouse size={size} />
    case 114: return <LuScale size={size} />
    case 115: return <LuFlaskConical size={size} />
    case 116: case 130: case 131: return <LuHandCoins size={size} />
    case 117: return <LuStamp size={size} />
    case 118: return <LuClipboardCheck size={size} />
    case 120: return <LuPackage size={size} />
    default: return <LuFileText size={size} />
  }
}

async function downloadBlob(url: string, filename: string): Promise<void> {
  const response = await apiClient.fetchAuthorized(url)
  if (!response.ok) throw new Error(`Download failed: ${response.status}`)
  const blob = await response.blob()
  const blobUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(blobUrl)
}

/**
 * VISTA OPERATIVA del expediente: el dosier como se trabaja, agrupado por jerarquía
 * (secciones plegables, descarga por ítem, lotes enlazados que se abren en línea).
 * Su hermana archivística (`ExpedienteElectronico`) lee el mismo fondo por folio.
 * Las dos comparten la cabecera de carpeta para que se reconozcan como el mismo
 * expediente visto de dos maneras.
 */
export default function DetalleExpedientePage() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { t } = useT()

  const [expediente, setExpediente] = useState<ExpedienteDetailResponse | null>(null)
  const [items, setItems] = useState<ExpedienteItemResponse[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [downloadingZip, setDownloadingZip] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const sentinelRef = useRef<HTMLDivElement | null>(null)

  // Carga inicial (página 1, server-side).
  useEffect(() => {
    if (!code) return
    let cancelled = false
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await expedienteService.getByCode(code, 1, PAGE_SIZE)
        if (cancelled) return
        setExpediente(data)
        setItems(data.items)
        setPage(1)
        setHasMore(data.hasMore)
        setCollapsed(new Set())
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'No se pudo cargar el expediente')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [code])

  // Carga de la siguiente página de grupos (server-side).
  const loadMore = useCallback(async () => {
    if (!code || loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      const next = page + 1
      const data = await expedienteService.getByCode(code, next, PAGE_SIZE)
      setItems(prev => [...prev, ...data.items])
      setPage(next)
      setHasMore(data.hasMore)
    } catch {
      /* el centinela reintenta al volver a entrar en viewport */
    } finally {
      setLoadingMore(false)
    }
  }, [code, page, hasMore, loadingMore])

  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !hasMore) return
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore() },
      { rootMargin: '400px' },
    )
    obs.observe(node)
    return () => obs.disconnect()
  }, [hasMore, loadMore])

  const handleDownloadZip = async () => {
    if (!expediente) return
    setDownloadingZip(true)
    try {
      await downloadBlob(
        expedienteService.getDownloadZipUrl(expediente.code),
        `${expediente.code}.zip`,
      )
      toaster.success({ description: t('expedientes.detail.downloadZipSuccess') })
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al descargar el ZIP' })
    } finally {
      setDownloadingZip(false)
    }
  }

  const toggleCollapse = (order: string) =>
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(order)) next.delete(order); else next.add(order)
      return next
    })

  // Un ítem queda oculto si algún ancestro (por prefijo de orden punteado) está colapsado.
  const isHidden = (order: string): boolean => {
    for (const o of collapsed) {
      if (order.startsWith(`${o}.`)) return true
    }
    return false
  }

  if (loading) {
    return (
      <VStack gap={4} py={20}>
        <Spinner size="lg" color={acc.color} />
        <Text fontSize="sm" color={themeColors.textMuted}>{t('expedientes.detail.loading')}</Text>
      </VStack>
    )
  }

  if (error || !expediente) {
    return (
      <VStack gap={4} py={20}>
        <Text fontSize="lg" color={themeColors.text}>{error ?? t('expedientes.detail.notFound')}</Text>
        <Box as="button" color={acc.deep} fontSize="sm" cursor="pointer" onClick={() => navigate('/app/identidad/expedientes')}>
          {t('expedientes.detail.backToSearch')}
        </Box>
      </VStack>
    )
  }

  const status = STATUS_CONFIG[expediente.status] ?? STATUS_CONFIG[0]!
  const visibleItems = items.filter(i => !isHidden(i.order))
  // El detalle no trae el tipo: un expediente de lote se reconoce por su ancla de traslado.
  const isBatch = items.some(i => i.itemType >= 100 && i.itemType < 120)
  const counts = [
    { value: expediente.totalFolios, label: 'folios' },
    { value: expediente.itemCount, label: 'ítems' },
    { value: expediente.topLevelCount, label: 'secciones' },
  ]

  return (
    <VStack gap={5} alignItems="stretch">
      {/* Vuelta al listado */}
      <Flex as="button" {...{ type: 'button' }} alignSelf="flex-start" align="center" gap={2} color={mineral.ink3} fontSize="13.5px" fontWeight={500}
        cursor="pointer" _hover={{ color: acc.deep }} transition="color .15s"
        onClick={() => navigate('/app/identidad/expedientes')}>
        <LuArrowLeft size={16} /> {t('expedientes.title')}
      </Flex>

      {/* Cabecera: la carpeta (misma que la vista archivística) */}
      <MotionBox
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        position="relative"
        bg={mineral.surface}
        border={`1px solid ${mineral.line}`}
        borderRadius="22px"
        boxShadow={shadows.md}
        overflow="hidden"
      >
        <Box position="absolute" left={0} top={0} bottom={0} w="5px" bg={acc.color} />

        <Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 360px' }}>
          <Box px={{ base: 6, md: 8 }} py={6} minW={0}>
            <Flex align="center" gap={2.5} wrap="wrap">
              <Flex align="center" gap={1.5} color={acc.deep}>
                <LuFolderTree size={14} />
                <Text fontFamily={fonts.mono} fontSize="10.5px" letterSpacing="0.14em" textTransform="uppercase">
                  Vista operativa · {isBatch ? 'Expediente de lote' : 'Expediente de minero'}
                </Text>
              </Flex>
              <Box px={2} py="2px" borderRadius="6px" fontSize="10.5px" fontWeight={700} textTransform="uppercase" letterSpacing="0.05em"
                bg={status.bg} color={status.color}>
                {status.label}
              </Box>
            </Flex>

            <Text fontFamily={fonts.mono} fontWeight={700} fontSize={{ base: '24px', md: '30px' }} color={mineral.ink} letterSpacing="-0.01em" mt={2} lineHeight="1.1">
              {expediente.code}
            </Text>
            {expediente.companyLegalName && (
              <Text fontFamily={fonts.display} fontWeight={700} fontSize="18px" color={mineral.ink2} letterSpacing="-0.01em" mt={1}>
                {expediente.companyLegalName}
              </Text>
            )}
            <Flex gap={4} mt={2} wrap="wrap" fontSize="12.5px" color={mineral.ink2}>
              {expediente.companyRuc && (
                <Text><Text as="span" color={mineral.ink4}>RUC </Text><Text as="span" fontFamily={fonts.mono}>{expediente.companyRuc}</Text></Text>
              )}
              {expediente.cuidCode && (
                <Text><Text as="span" color={mineral.ink4}>CUID </Text><Text as="span" fontFamily={fonts.mono}>{expediente.cuidCode}</Text></Text>
              )}
              {expediente.approvedAt && (
                <Text><Text as="span" color={mineral.ink4}>{t('expedientes.detail.approvedAt')} </Text>{formatDateTime(expediente.approvedAt)}</Text>
              )}
            </Flex>

            <Flex mt={5} pt={4} borderTop={`1px dashed ${mineral.lineStrong}`} wrap="wrap" rowGap={3}>
              {counts.map((c, i) => (
                <Box key={c.label} pr={6} mr={6} py={0.5} borderRight={i < counts.length - 1 ? `1px solid ${mineral.line}` : 'none'}>
                  <Text fontFamily={fonts.display} fontWeight={800} fontSize="26px" color={mineral.ink} lineHeight="1" letterSpacing="-0.03em">
                    <AnimatedNumber value={c.value} />
                  </Text>
                  <Text fontSize="12px" color={mineral.ink3} mt={1}>{c.label}</Text>
                </Box>
              ))}
            </Flex>
          </Box>

          {/* Integridad + acciones */}
          <Flex direction="column" px={{ base: 6, md: 7 }} py={6} bg={mineral.surfaceAlt}
            borderLeft={{ base: 'none', xl: `1px solid ${mineral.line}` }} borderTop={{ base: `1px solid ${mineral.line}`, xl: 'none' }}>
            <Flex align="center" gap={2} color={expediente.chainHead ? mineral.sabanaDeep : mineral.ink3}>
              <LuShieldCheck size={16} />
              <Text fontSize="13px" fontWeight={600}>{expediente.chainHead ? 'Cadena de hashes encadenada' : 'Cadena sin calcular'}</Text>
            </Flex>
            {expediente.chainHead && (
              <Box mt={2} px={3} py={2} borderRadius="10px" bg="#fff" border={`1px dashed ${mineral.lineStrong}`}>
                <Text fontSize="10px" fontFamily={fonts.mono} letterSpacing="0.14em" color={mineral.ink3}>CABEZA DE LA CADENA</Text>
                <Text fontFamily={fonts.mono} fontSize="12.5px" color={mineral.ink} mt={0.5} truncate title={expediente.chainHead}>
                  {expediente.chainHead}
                </Text>
              </Box>
            )}

            <Box flex={1} minH={4} />

            {/* Puente a la vista ARCHIVÍSTICA (índice digital foliado, series
                documentales y versiones selladas). Ésta sigue siendo la operativa. */}
            <Flex as="button" {...{ type: 'button' }} mt={4} align="center" justify="center" gap={2} h="40px" borderRadius="11px"
              bg={brand.primary} color="#fff" fontSize="13.5px" fontWeight={600} cursor="pointer"
              _hover={{ bg: mineral.ocreDark }} transition="background .15s"
              onClick={() => navigate(`/app/identidad/expedientes/${encodeURIComponent(expediente.code)}/electronico`)}>
              <LuStamp size={16} />
              {t('expedientes.detail.openElectronico')}
              <LuArrowUpRight size={14} />
            </Flex>
            <Flex as="button" {...{ type: 'button', disabled: downloadingZip }} mt={2} align="center" justify="center" gap={2} h="40px" borderRadius="11px"
              border={`1px solid ${mineral.line}`} bg="#fff" color={mineral.ink2} fontSize="13.5px" fontWeight={600}
              cursor={downloadingZip ? 'progress' : 'pointer'} _hover={downloadingZip ? {} : { borderColor: acc.color, color: acc.deep }} transition="all .15s"
              onClick={handleDownloadZip}>
              <motion.span animate={downloadingZip ? { y: [0, 3, 0] } : { y: 0 }}
                transition={downloadingZip ? { duration: 0.7, repeat: Infinity } : { duration: 0.2 }} style={{ display: 'flex' }}>
                <LuDownload size={16} />
              </motion.span>
              {downloadingZip ? t('expedientes.detail.downloadingZip') : t('expedientes.detail.downloadZip')}
            </Flex>
          </Flex>
        </Grid>
      </MotionBox>

      {/* Línea de tiempo del expediente */}
      <Box bg={mineral.surface} border={`1px solid ${mineral.line}`} borderRadius="20px" boxShadow={shadows.sm} px={{ base: 4, md: 6 }} py={5}>
        <Flex alignItems="baseline" gap={3} mb={4} justifyContent="space-between" flexWrap="wrap">
          <Text fontFamily={fonts.display} fontWeight={700} fontSize="18px" color={mineral.ink} letterSpacing="-0.01em">
            {t('expedientes.detail.timelineTitle')}
          </Text>
          <Text fontFamily={fonts.mono} fontSize="12px" color={mineral.ink3}>
            {expediente.topLevelCount > 0
              ? `${Math.min(page * PAGE_SIZE, expediente.topLevelCount)} de ${expediente.topLevelCount} secciones`
              : ''}
          </Text>
        </Flex>

        <Box>
          {visibleItems.map((item, idx) => (
            <ExpedienteItemRowWithExpand
              key={item.id}
              item={item}
              index={idx}
              isLast={idx === visibleItems.length - 1 && !hasMore}
              expedienteCode={expediente.code}
              collapsed={collapsed.has(item.order)}
              onToggleCollapse={() => toggleCollapse(item.order)}
            />
          ))}
        </Box>

        {hasMore && (
          <Flex ref={sentinelRef} justifyContent="center" alignItems="center" py={5} gap={2}>
            <Spinner size="sm" color={acc.color} />
            <Text fontSize="xs" color={mineral.ink3}>Cargando más secciones…</Text>
          </Flex>
        )}
      </Box>
    </VStack>
  )
}

// Chip de folios: grupo (suma) si tiene hijos; folios propios si es hoja.
function FolioChip({ item }: { item: ExpedienteItemResponse }) {
  const isGroup = item.childCount > 0
  const n = isGroup ? item.groupFolioCount : item.folioCount
  if (n <= 0 && !isGroup) return null
  return (
    <Flex
      alignItems="center" gap={1} px={2} h="22px" borderRadius="6px" flexShrink={0}
      bg={isGroup ? acc.soft : mineral.sunk}
      color={isGroup ? acc.deep : mineral.ink3}
    >
      {isGroup ? <LuFolderOpen size={11} /> : <LuFiles size={11} />}
      <Text fontSize="10.5px" fontWeight={600} fontFamily={fonts.mono}>{folioLabel(n)}</Text>
    </Flex>
  )
}

/**
 * Envuelve la fila para que un BatchExpedienteLink cargue (lazy) e inserte los
 * ítems del expediente de lote enlazado. El resto de tipos se renderiza igual.
 */
function ExpedienteItemRowWithExpand({
  item,
  index,
  isLast,
  expedienteCode,
  collapsed,
  onToggleCollapse,
}: {
  item: ExpedienteItemResponse
  index: number
  isLast: boolean
  expedienteCode: string
  collapsed: boolean
  onToggleCollapse: () => void
}) {
  const isBatchLink = item.itemType === EXPEDIENTE_ITEM_TYPE_BATCH_LINK
  const isGroup = item.childCount > 0
  const [expanded, setExpanded] = useState(false)
  const [childItems, setChildItems] = useState<ExpedienteItemResponse[] | null>(null)
  const [loadingChildren, setLoadingChildren] = useState(false)
  const [childError, setChildError] = useState<string | null>(null)

  const handleDownload = async () => {
    if (!item.primaryFile) return
    try {
      const url = expedienteService.getItemFileUrl(expedienteCode, item.id)
      await downloadBlob(url, item.primaryFile.originalFileName)
    } catch (e) {
      toaster.error({
        title: 'No se pudo descargar el archivo',
        description: e instanceof Error ? e.message : 'Error desconocido',
      })
    }
  }

  const handleBatchToggle = async () => {
    const next = !expanded
    setExpanded(next)
    if (next && childItems === null && item.linkedExpedienteCode) {
      setLoadingChildren(true)
      setChildError(null)
      try {
        const linked = await expedienteService.getByCode(item.linkedExpedienteCode, 1, 200)
        setChildItems(linked.items)
      } catch (e) {
        setChildError(e instanceof Error ? e.message : 'No se pudo cargar el expediente del traslado')
      } finally {
        setLoadingChildren(false)
      }
    }
  }

  // Para grupos reales (con hijos dentro de este expediente) el chevron colapsa
  // la sección; para un BatchExpedienteLink hace lazy-load del expediente de lote.
  const expandable = isBatchLink ? !!item.linkedExpedienteCode : isGroup
  const isOpen = isBatchLink ? expanded : !collapsed
  const onToggle = isBatchLink ? handleBatchToggle : onToggleCollapse

  return (
    <>
      <ExpedienteItemRow
        item={item}
        index={index}
        isLast={isLast && !(isBatchLink && expanded)}
        onDownload={handleDownload}
        expandable={expandable}
        expanded={isOpen}
        onToggleExpand={onToggle}
      />
      <AnimatePresence initial={false}>
        {isBatchLink && expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }} style={{ overflow: 'hidden' }}>
            <Box ml={`${(item.depth + 1) * 28 + 4}px`} my={1} pl={4} borderLeft={`2px dashed ${mineral.oro}66`}>
              {loadingChildren && (
                <Text fontSize="xs" color={mineral.ink3} px={3} py={2}>Cargando ítems del traslado…</Text>
              )}
              {childError && (
                <Text fontSize="xs" color={mineral.aji} px={3} py={2}>{childError}</Text>
              )}
              {childItems && childItems.length === 0 && (
                <Text fontSize="xs" color={mineral.ink3} px={3} py={2}>El expediente del traslado todavía no tiene ítems.</Text>
              )}
              {childItems && childItems.map((child, cIdx) => (
                <ExpedienteItemRow
                  key={child.id}
                  item={child}
                  index={cIdx}
                  isLast={cIdx === childItems.length - 1}
                  onDownload={async () => {
                    if (!child.primaryFile || !item.linkedExpedienteCode) return
                    try {
                      const url = expedienteService.getItemFileUrl(item.linkedExpedienteCode, child.id)
                      await downloadBlob(url, child.primaryFile.originalFileName)
                    } catch (e) {
                      toaster.error({
                        title: 'No se pudo descargar el archivo',
                        description: e instanceof Error ? e.message : 'Error desconocido',
                      })
                    }
                  }}
                />
              ))}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

/**
 * Una fila de la línea de tiempo: nodo (ícono en el color de su familia) sobre el
 * hilo vertical, número de orden, título y metadatos, folios y descarga. Los
 * subítems se sangran y cuelgan del mismo hilo.
 */
function ExpedienteItemRow({
  item,
  index,
  isLast,
  onDownload,
  expandable,
  expanded,
  onToggleExpand,
}: {
  item: ExpedienteItemResponse
  index: number
  isLast: boolean
  onDownload: () => void
  expandable?: boolean
  expanded?: boolean
  onToggleExpand?: () => void
}) {
  const { t } = useT()
  const color = itemColor(item.itemType)
  const isGroup = item.childCount > 0
  const isRoot = item.depth === 0
  const nodeSize = isRoot ? 34 : 28

  return (
    <MotionBox
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.18, delay: Math.min(index, 12) * 0.012 }}
      pl={`${item.depth * 28}px`}
    >
      <Flex
        role="group"
        gap={3}
        align="stretch"
        cursor={expandable ? 'pointer' : 'default'}
        onClick={expandable ? onToggleExpand : undefined}
      >
        {/* Hilo + nodo */}
        <Flex direction="column" align="center" w="34px" flexShrink={0}>
          <Flex w={`${nodeSize}px`} h={`${nodeSize}px`} mt={isRoot ? 2 : 2.5} borderRadius="10px" align="center" justify="center" flexShrink={0}
            bg={isRoot ? color : `${color}1f`} color={isRoot ? '#fff' : color} transition="transform .15s"
            _groupHover={{ transform: 'scale(1.06)' }}>
            <ItemTypeIcon itemType={item.itemType} size={isRoot ? 16 : 14} />
          </Flex>
          {!isLast && <Box flex={1} w="2px" minH="10px" mt={1} bg={mineral.line} />}
        </Flex>

        {/* Contenido */}
        <Flex flex={1} minW={0} align="center" gap={3} py={2.5} px={3} borderRadius="12px" transition="background .15s"
          _groupHover={{ bg: mineral.surfaceAlt }}>
          <Box flex="1" minW={0}>
            <Flex alignItems="center" gap={2} flexWrap="wrap">
              <Text fontFamily={fonts.mono} fontSize="11.5px" fontWeight={600} color={mineral.ink3}>{item.order}</Text>
              <Text fontSize={isRoot ? '14.5px' : '13.5px'} fontWeight={isGroup || isRoot ? 700 : 600} color={mineral.ink}>
                {item.title}
              </Text>
              <Box px={1.5} py="1px" borderRadius="5px" fontSize="9.5px" fontWeight={700} textTransform="uppercase" letterSpacing="0.04em"
                bg={`${color}14`} color={color}>
                {item.itemTypeName}
              </Box>
            </Flex>
            <Flex columnGap={1.5} mt={0.5} fontSize="12px" color={mineral.ink3} flexWrap="wrap">
              <Text>{formatDateTime(item.occurredAt)}</Text>
              {item.registeredByUserName && (
                <Text>· {t('expedientes.detail.registeredBy')} {item.registeredByUserName}</Text>
              )}
              {item.primaryFile && (
                <Text fontFamily={fonts.mono} fontSize="11.5px" truncate maxW="420px" title={item.primaryFile.originalFileName}>
                  · {item.primaryFile.originalFileName} · {formatFileSize(item.primaryFile.fileSize)}
                </Text>
              )}
              {isGroup && (
                <Text>· {item.childCount} {item.childCount === 1 ? 'subítem' : 'subítems'}</Text>
              )}
            </Flex>
          </Box>

          <FolioChip item={item} />

          {item.primaryFile && (
            <Flex
              as="button" {...{ type: 'button', title: t('expedientes.detail.downloadFile'), 'aria-label': t('expedientes.detail.downloadFile') }}
              align="center" justify="center" w="34px" h="34px" borderRadius="10px" flexShrink={0}
              border={`1px solid ${mineral.line}`} bg="#fff" color={mineral.ink3} cursor="pointer"
              _hover={{ borderColor: acc.color, color: acc.deep }} transition="all .15s"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); onDownload() }}
            >
              <LuDownload size={15} />
            </Flex>
          )}
          {expandable && (
            <Box color={mineral.ink3} display="flex" transition="transform .2s" transform={expanded ? 'rotate(90deg)' : 'none'} flexShrink={0}>
              <LuChevronRight size={16} />
            </Box>
          )}
        </Flex>
      </Flex>
    </MotionBox>
  )
}
